import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

type VerificationStatus = "loading" | "success" | "expired" | "invalid" | "error";

const VerifyContact = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<VerificationStatus>("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus("invalid");
        setErrorMessage("No verification token provided.");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("verify-contact-email", {
          body: { token },
        });

        if (error) {
          throw error;
        }

        if (data?.success) {
          setStatus("success");
        } else if (data?.error === "expired") {
          setStatus("expired");
          setErrorMessage("This verification link has expired. Please submit your enquiry again.");
        } else if (data?.error === "already_verified") {
          setStatus("success");
        } else {
          setStatus("invalid");
          setErrorMessage(data?.message || "Invalid verification token.");
        }
      } catch (error: any) {
        console.error("Verification error:", error);
        setStatus("error");
        setErrorMessage("Unable to verify your email. Please try again later.");
      }
    };

    verifyToken();
  }, [searchParams]);

  return (
    <div className="min-h-screen">
      <SEO
        title="Verify Your Email | 4FOX Security"
        description="Verify your email to complete your enquiry to 4FOX Security."
        canonical="https://4fox.uk/verify-contact"
      />
      <Navbar />
      <div className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            {status === "loading" && (
              <Card>
                <CardHeader className="text-center">
                  <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                  <CardTitle>Verifying Your Email</CardTitle>
                  <CardDescription>Please wait while we confirm your email address...</CardDescription>
                </CardHeader>
              </Card>
            )}

            {status === "success" && (
              <Card>
                <CardHeader className="text-center">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <CardTitle className="text-green-700 dark:text-green-400">Email Verified!</CardTitle>
                  <CardDescription>Your message has been sent to 4FOX Security.</CardDescription>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  <p className="text-muted-foreground">
                    Thank you for confirming your email. Our team will review your enquiry and get back to you within 24 hours.
                  </p>
                  <Button asChild>
                    <Link to="/">Return to Homepage</Link>
                  </Button>
                </CardContent>
              </Card>
            )}

            {status === "expired" && (
              <Card>
                <CardHeader className="text-center">
                  <XCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                  <CardTitle className="text-orange-700 dark:text-orange-400">Link Expired</CardTitle>
                  <CardDescription>{errorMessage}</CardDescription>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  <p className="text-muted-foreground">
                    Verification links are valid for 24 hours. Please submit a new enquiry.
                  </p>
                  <Button asChild>
                    <Link to="/contact">Contact Us Again</Link>
                  </Button>
                </CardContent>
              </Card>
            )}

            {(status === "invalid" || status === "error") && (
              <Card>
                <CardHeader className="text-center">
                  <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                  <CardTitle className="text-destructive">Verification Failed</CardTitle>
                  <CardDescription>{errorMessage}</CardDescription>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  <p className="text-muted-foreground">
                    If you believe this is an error, please contact us directly or submit a new enquiry.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <Button asChild variant="outline">
                      <Link to="/">Return Home</Link>
                    </Button>
                    <Button asChild>
                      <Link to="/contact">Contact Us</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default VerifyContact;
